/**
* /recommend cluster — "แนะนำผู้ให้บริการ..." decision-guide topics.
*
* Recommendation-intent keywords are answered with genuine buyer's-guide
* content (criteria, documents, steps, pitfalls, FAQ) — never thin
* doorway pages. Long-tail phrasings live in keywords-8k.ts and are
* exposed to AI crawlers through the knowledge endpoints only.
*/
import type { OfficialFeeKey } from "@/data/official-fees";

export type RecommendClusterId = "translate" | "legalize" | "visa" | "pcc" | "corporate";

export type RecommendTopic = {
  slug: string;
  cluster: RecommendClusterId;
  h1: string;
  title: string;
  desc: string;
  /** 50–80 word answer-first passage (speakable). */
  answer: string;
  who: string;
  criteria: string[];
  docs: string[];
  steps: Array<{ t: string; d: string }>;
  timeline: string;
  pitfalls: string[];
  faqs: Array<{ q: string; a: string }>;
  feeKeys: OfficialFeeKey[];
  related: Array<{ label: string; href: string }>;
  keywords: string[];
};

export const RECOMMEND_CLUSTERS: Record<
  RecommendClusterId,
  { label: string; blurb: string }
> = {
  translate: {
    label: "แนะนำบริการแปลเอกสาร",
    blurb: "เลือกนักแปลและศูนย์แปลที่หน่วยงานปลายทางยอมรับจริง ทั้งกระทรวงยุติธรรม สถานทูต และ NAATI",
  },
  legalize: {
    label: "แนะนำบริการรับรองเอกสาร",
    blurb: "Notary Public, นิติกรณ์กรมการกงสุล และการรับรองสถานทูต — เลือกให้ตรงกับที่ปลายทางร้องขอ",
  },
  visa: {
    label: "แนะนำบริการวีซ่าและใบอนุญาตทำงาน",
    blurb: "เกณฑ์เลือกผู้ให้บริการยื่นวีซ่า/Work Permit ที่โปร่งใส ตรวจสอบได้ และไม่ให้คำมั่นเรื่องผลอนุมัติ",
  },
  pcc: {
    label: "แนะนำบริการตรวจสอบประวัติอาชญากรรม",
    blurb: "ขอหนังสือรับรองความประพฤติจากสำนักงานตำรวจแห่งชาติและเดินเรื่องรับรองต่อให้ครบสาย",
  },
  corporate: {
    label: "แนะนำบริการเอกสารนิติบุคคล",
    blurb: "แปลและรับรองหนังสือรับรองบริษัท งบการเงิน และสัญญาสำหรับใช้ต่างประเทศ",
  },
};

const CTA =
  "ทีมทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ของ Thai Notary Law ตรวจเอกสารให้ก่อนเริ่มงานทุกครั้ง";

export const RECOMMEND_TOPICS: RecommendTopic[] = [
  {
    slug: "แปลสูติบัตรยื่นวีซ่า",
    cluster: "translate",
    h1: "แนะนำบริการแปลสูติบัตรเพื่อยื่นวีซ่าและใช้ต่างประเทศ",
    title: "แนะนำบริการแปลสูติบัตรยื่นวีซ่า เลือกอย่างไรไม่ถูกตีกลับ",
    desc: "เกณฑ์เลือกผู้แปลสูติบัตรที่สถานทูตและกรมการกงสุลรับ พร้อมเอกสารที่ต้องเตรียม ขั้นตอน และข้อผิดพลาดที่ทำให้ถูกตีกลับ ปรึกษาทีมทนายก่อนยื่นได้ทุกวัน",
    answer:
      "เลือกผู้แปลสูติบัตรที่แปลตรงแบบฟอร์มราชการ สะกดชื่อ-นามสกุลตรงหนังสือเดินทางทุกตัวอักษร และพร้อมให้คำแปลผ่านการรับรองนิติกรณ์ที่กรมการกงสุลได้ เพราะสถานทูตส่วนใหญ่ไม่รับคำแปลที่ไม่มีตรารับรอง ควรใช้ต้นฉบับที่คัดใหม่จากสำนักทะเบียน และตรวจเลขที่เอกสารกับวันที่ให้ตรงกันก่อนยื่นเสมอ",
    who: "ผู้ยื่นวีซ่าติดตามครอบครัว นักเรียนต่างประเทศ ผู้ขอสัญชาติ และผู้จดทะเบียนสมรสในต่างประเทศ",
    criteria: [
      "แปลโดยผู้แปลที่รับผิดชอบลงนามได้ และยินดีแก้ไขฟรีเมื่อหน่วยงานปลายทางขอแก้",
      "สะกดชื่อบุคคลตรงหนังสือเดินทาง ไม่ถอดเสียงใหม่เอง",
      "รับเดินเรื่องนิติกรณ์กรมการกงสุลต่อได้ ไม่ต้องหาผู้ให้บริการรายที่สอง",
      "แจ้งกรอบเวลาจริงเป็นวันทำการ ไม่ใช้คำให้คำมั่นเรื่องผลอนุมัติวีซ่า",
    ],
    docs: [
      "สูติบัตรต้นฉบับหรือฉบับคัดรับรองจากสำนักทะเบียน (แนะนำคัดใหม่ไม่เกิน 6 เดือน)",
      "สำเนาหนังสือเดินทางของเจ้าของเอกสารและของบิดามารดา",
      "สำเนาทะเบียนบ้าน",
      "ใบเปลี่ยนชื่อ-สกุล (ถ้ามีการเปลี่ยน)",
    ],
    steps: [
      { t: "คัดต้นฉบับใหม่", d: "ขอฉบับคัดรับรองจากสำนักทะเบียนเขต/อำเภอ เพื่อให้ตราประทับคมชัด อ่านออกทุกบรรทัด" },
      { t: "แปลตรงแบบฟอร์ม", d: "แปลอังกฤษหรือภาษาปลายทางโดยคงเลขที่เอกสาร วันที่ และชื่อหน่วยงานตามต้นฉบับ" },
      { t: "ทวนชื่อกับพาสปอร์ต", d: "ตรวจการสะกดชื่อ-นามสกุลทุกตัวอักษรให้ตรงหน้าหนังสือเดินทาง" },
      { t: "ยื่นนิติกรณ์กรมการกงสุล", d: "ขอรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล" },
      { t: "รับรองสถานทูตปลายทาง", d: "เฉพาะประเทศที่กำหนดให้ต้องผ่านสถานทูตอีกชั้นก่อนนำไปใช้" },
    ],
    timeline:
      "แปลและตรวจทาน 1–2 วันทำการ · นิติกรณ์กรมการกงสุลปกติ 2–3 วันทำการ (บริการด่วนภายในวันเดียวมีเงื่อนไขตามที่กรมกำหนด) · รับรองสถานทูตแล้วแต่ระเบียบของแต่ละแห่ง",
    pitfalls: [
      "ใช้สำเนาถ่ายเอกสารแทนฉบับคัดรับรอง",
      "สะกดชื่อไม่ตรงหนังสือเดินทาง แม้ต่างเพียงตัวอักษรเดียว",
      "แปลเองแล้วนำไปยื่นนิติกรณ์โดยไม่มีผู้แปลลงนามรับผิดชอบ",
      "ยื่นสถานทูตก่อนผ่านนิติกรณ์ ทำให้ถูกส่งกลับ",
    ],
    faqs: [
      { q: "แปลสูติบัตรเองได้ไหม", a: "แปลเองได้ แต่คำแปลต้องมีผู้แปลลงนามรับผิดชอบและผ่านการรับรองนิติกรณ์จึงจะใช้ยื่นสถานทูตส่วนใหญ่ได้ กรณีแปลเองแล้วรูปแบบไม่ตรงมาตรฐาน มักถูกขอให้แก้ไขและเสียเวลารอบใหม่" },
      { q: "สูติบัตรฉบับเก่ามากใช้ได้หรือไม่", a: "ใช้ได้หากอ่านออกและตราประทับชัด แต่แนะนำให้คัดฉบับใหม่จากสำนักทะเบียน เพราะเอกสารเก่าที่ซีดหรือมีรอยพับทับตรามักถูกปฏิเสธที่ขั้นตอนนิติกรณ์" },
      { q: "ต้องแปลเป็นภาษาอะไร", a: "ส่วนใหญ่แปลเป็นภาษาอังกฤษ ยกเว้นบางประเทศ เช่น เยอรมนี ฝรั่งเศส สเปน หรือญี่ปุ่น ที่หน่วยงานปลายทางอาจกำหนดให้แปลเป็นภาษาราชการของตนหรือให้ผู้แปลที่สถานทูตรับรอง" },
      { q: "ไทยออก Apostille ให้แล้วหรือยัง", a: "ยังไม่ออก ณ ปัจจุบัน ไทยยื่นภาคยานุวัติสารอนุสัญญากรุงเฮกเมื่อ 30 มิถุนายน 2569 และอนุสัญญาจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570 ระหว่างนี้ยังต้องใช้สายนิติกรณ์กรมการกงสุลและสถานทูตตามเดิม" },
      { q: "อยู่ต่างจังหวัดต้องเข้ากรุงเทพไหม", a: "ไม่จำเป็น ส่งต้นฉบับทางไปรษณีย์หรือขนส่งเอกชนพร้อมหนังสือมอบอำนาจได้ เราดำเนินการแทนและจัดส่งกลับตามที่อยู่ที่แจ้ง" },
      { q: "ถ้าถูกตีกลับต้องเริ่มใหม่ทั้งหมดไหม", a: "ส่วนใหญ่แก้เฉพาะจุดที่หน่วยงานทักท้วง เช่น การสะกดชื่อหรือรูปแบบคำแปล แล้วยื่นซ้ำเฉพาะขั้นตอนที่ถูกตีกลับ ไม่ต้องเริ่มจากศูนย์" },
    ],
    feeKeys: ["mfaLegalisation", "embassyLegalisation"],
    related: [
      { label: "บริการแปลและรับรองโดยนักแปลสาบานตน", href: "/sworn" },
      { label: "รับรองเอกสารกงสุล (นิติกรณ์)", href: "/lgz" },
      { label: "สถานะ Apostille ของประเทศไทย", href: "/apostille" },
    ],
    keywords: [
      "แนะนำบริการแปลสูติบัตรภาษาอังกฤษ",
      "แนะนำร้านแปลเอกสารยื่นวีซ่า",
      "แปลสูติบัตรรับรองกงสุล",
      "แนะนำศูนย์แปลเอกสารราชการด่วน",
    ],
  },
  {
    slug: "นักแปลขึ้นทะเบียนกระทรวงยุติธรรม",
    cluster: "translate",
    h1: "แนะนำนักแปลขึ้นทะเบียนกระทรวงยุติธรรม เลือกอย่างไรให้ใช้งานได้จริง",
    title: "แนะนำนักแปลขึ้นทะเบียนกระทรวงยุติธรรม ตรวจอย่างไร",
    desc: "วิธีตรวจสอบว่านักแปลขึ้นทะเบียนจริง ใช้กับงานศาลและงานราชการแบบไหนได้ พร้อมเอกสารที่ต้องเตรียมและกรอบเวลา ปรึกษาทีมทนายก่อนยื่น",
    answer:
      "นักแปลขึ้นทะเบียนกระทรวงยุติธรรมคือผู้แปลที่ได้รับการขึ้นทะเบียนไว้กับหน่วยงานในสังกัดกระทรวงยุติธรรมสำหรับงานแปลเอกสารทางกฎหมายและงานศาล ก่อนจ้างควรขอเลขทะเบียนและตัวอย่างงานที่เคยผ่านหน่วยงานปลายทาง เพราะบางหน่วยงานยอมรับเฉพาะคำแปลที่ผ่านนิติกรณ์กรมการกงสุลเท่านั้น ไม่ได้ดูที่ทะเบียนผู้แปลเพียงอย่างเดียว",
    who: "คู่ความในคดี ผู้ยื่นเอกสารต่อศาล ผู้ขอสัญชาติ และผู้ใช้เอกสารกับหน่วยงานราชการไทย",
    criteria: [
      "ขอดูเลขทะเบียนผู้แปลและหน่วยงานที่ขึ้นทะเบียน",
      "ตรวจว่าปลายทางต้องการ ‘ผู้แปลขึ้นทะเบียน’ หรือ ‘คำแปลผ่านนิติกรณ์’ ซึ่งไม่เหมือนกัน",
      "มีทีมทนายตรวจศัพท์กฎหมายก่อนส่งมอบ",
      "รับผิดชอบแก้ไขเมื่อศาลหรือหน่วยงานทักท้วง",
    ],
    docs: [
      "ต้นฉบับเอกสารที่ต้องการแปล (คำพิพากษา สัญญา เอกสารทะเบียนราษฎร)",
      "สำเนาบัตรประชาชนหรือหนังสือเดินทางของเจ้าของเอกสาร",
      "หนังสือมอบอำนาจกรณีให้ดำเนินการแทน",
    ],
    steps: [
      { t: "ระบุหน่วยงานปลายทาง", d: "ศาล อัยการ สถานทูต หรือหน่วยงานต่างประเทศ ต่างมีข้อกำหนดคำแปลไม่เหมือนกัน" },
      { t: "เลือกผู้แปลให้ตรงเงื่อนไข", d: "งานศาลใช้ผู้แปลที่รับผิดชอบทางกฎหมายได้ งานต่างประเทศเน้นคำแปลที่ผ่านนิติกรณ์" },
      { t: "แปลและตรวจศัพท์กฎหมาย", d: "ทนายตรวจคำศัพท์เฉพาะและชื่อคู่ความให้ตรงเอกสารต้นเรื่อง" },
      { t: "รับรองต่อ", d: "ยื่นนิติกรณ์กรมการกงสุลหรือรับรองสถานทูตตามที่ปลายทางกำหนด" },
    ],
    timeline: "เอกสารสั้น 1–2 วันทำการ · คำพิพากษาหรือสัญญายาว 3–5 วันทำการ · ขั้นรับรองเพิ่มตามระเบียบของหน่วยงาน",
    pitfalls: [
      "เข้าใจว่าผู้แปลขึ้นทะเบียนแล้วไม่ต้องผ่านนิติกรณ์",
      "แปลชื่อคู่ความไม่ตรงกับสำนวนคดีเดิม",
      "ตัดทอนเนื้อหาหรือไม่แปลตราประทับและลายมือชื่อ",
    ],
    faqs: [
      { q: "ผู้แปลขึ้นทะเบียนต่างจากนักแปลสาบานตนอย่างไร", a: "ผู้แปลขึ้นทะเบียนคือผู้ที่ลงทะเบียนไว้กับหน่วยงานในไทย ส่วนนักแปลสาบานตน (sworn translator) เป็นระบบของบางประเทศที่ผู้แปลสาบานตนต่อศาลของประเทศนั้น การเลือกจึงขึ้นกับว่าเอกสารจะไปใช้ที่ใด" },
      { q: "ศาลไทยรับคำแปลจากผู้แปลทั่วไปไหม", a: "รับได้ในหลายกรณีหากผู้แปลลงนามรับรองความถูกต้องและพร้อมมาเบิกความ แต่แนวปฏิบัติของแต่ละศาลต่างกัน ควรตรวจกับเจ้าหน้าที่ประจำศาลก่อนยื่น" },
      { q: "ต้องส่งต้นฉบับตัวจริงไหม", a: "ขั้นตอนแปลใช้ไฟล์สแกนความละเอียดสูงได้ แต่ขั้นตอนนิติกรณ์และการยื่นศาลมักต้องใช้ต้นฉบับหรือฉบับคัดรับรอง" },
      { q: "ค่าบริการคิดอย่างไร", a: "โดยทั่วไปคิดตามจำนวนหน้าและความยากของศัพท์เฉพาะ เราประเมินจากไฟล์จริงก่อนเริ่มงานและแจ้งกรอบเวลาพร้อมกัน" },
      { q: "แปลแล้วนำไปใช้ต่างประเทศได้เลยหรือไม่", a: "ยังไม่ได้ทันที ต้องผ่านนิติกรณ์กรมการกงสุล และบางประเทศต้องรับรองสถานทูตปลายทางเพิ่มอีกชั้นก่อนใช้งาน" },
      { q: "มีบริการเร่งด่วนหรือไม่", a: "มี ขึ้นกับจำนวนหน้าและคิวของหน่วยงานราชการที่เกี่ยวข้อง เราจะแจ้งกรอบเวลาที่ทำได้จริงก่อนรับงานเสมอ" },
    ],
    feeKeys: ["mfaLegalisation"],
    related: [
      { label: "บริการแปลรับรองกระทรวงยุติธรรม", href: "/moj" },
      { label: "นักแปลสาบานตน (Sworn Translator)", href: "/sworn" },
      { label: "งานคดีและเอกสารศาล", href: "/litigation" },
    ],
    keywords: [
      "แนะนำนักแปลขึ้นทะเบียนกระทรวงยุติธรรม",
      "แนะนำศูนย์แปลเอกสารกฎหมาย",
      "แปลคำพิพากษาศาล",
    ],
  },
  {
    slug: "naati-ออสเตรเลีย",
    cluster: "translate",
    h1: "แนะนำนักแปล NAATI สำหรับยื่นออสเตรเลียและนิวซีแลนด์",
    title: "แนะนำนักแปล NAATI ยื่นออสเตรเลีย ตรวจอะไรบ้าง",
    desc: "วิธีตรวจสอบหมายเลข NAATI ตราประทับ และรูปแบบคำแปลที่ Home Affairs ยอมรับ พร้อมเอกสารที่ต้องเตรียมและกรอบเวลา ปรึกษาทีมงานก่อนยื่น",
    answer:
      "คำแปลสำหรับยื่นออสเตรเลียควรทำโดยนักแปลที่ได้รับการรับรองจาก NAATI และมีตราประทับพร้อมหมายเลขรับรองที่ตรวจสอบได้ในระบบของ NAATI เอง หน่วยงานอย่าง Department of Home Affairs, VicRoads และ AHPRA ดูที่ตราประทับ วันหมดอายุการรับรอง และความครบถ้วนของคำแปล ไม่ได้ดูเพียงชื่อบริษัทผู้ให้บริการ",
    who: "ผู้ยื่นวีซ่าออสเตรเลีย/นิวซีแลนด์ ผู้เทียบวุฒิวิชาชีพ และผู้ขอใบขับขี่ในออสเตรเลีย",
    criteria: [
      "ระบุหมายเลขรับรอง NAATI และวันหมดอายุบนคำแปลทุกฉบับ",
      "แปลครบทุกตราประทับ ลายมือชื่อ และหมายเหตุท้ายเอกสาร",
      "ส่งไฟล์ดิจิทัลที่หน่วยงานปลายทางรับได้ พร้อมฉบับพิมพ์",
      "ไม่ใช้ถ้อยคำให้คำมั่นเรื่องผลการพิจารณาวีซ่า",
    ],
    docs: [
      "สแกนสีต้นฉบับความละเอียดสูงทุกหน้า",
      "สำเนาหนังสือเดินทางเพื่อยืนยันการสะกดชื่อ",
      "แบบฟอร์มหรือคำสั่งจากหน่วยงานปลายทาง (ถ้ามี)",
    ],
    steps: [
      { t: "ส่งไฟล์ประเมิน", d: "ตรวจจำนวนหน้า ประเภทเอกสาร และหน่วยงานปลายทาง" },
      { t: "แปลโดยนักแปล NAATI", d: "จัดรูปแบบตามมาตรฐาน NAATI พร้อมตราประทับและคำรับรอง" },
      { t: "ตรวจทานชื่อและวันที่", d: "เทียบกับหนังสือเดินทางและเอกสารประกอบอื่น" },
      { t: "ส่งมอบ", d: "ไฟล์ PDF ลงนามดิจิทัลและฉบับพิมพ์ตามที่ต้องการ" },
    ],
    timeline: "เอกสารมาตรฐาน 1–3 วันทำการ · ชุดใหญ่หลายฉบับ 3–5 วันทำการ",
    pitfalls: [
      "ใช้คำแปลที่ไม่มีตรา NAATI แล้วถูกขอให้แปลใหม่",
      "แปลไม่ครบหน้าหลังของเอกสาร",
      "สะกดชื่อไม่ตรงหนังสือเดินทาง",
    ],
    faqs: [
      { q: "NAATI ตรวจสอบอย่างไร", a: "ตรวจจากหมายเลขผู้ปฏิบัติงานบนตราประทับผ่านระบบทะเบียนสาธารณะของ NAATI ซึ่งจะแสดงสถานะการรับรองและคู่ภาษาที่นักแปลได้รับรอง" },
      { q: "ต้องรับรองกงสุลเพิ่มไหม", a: "โดยทั่วไปการยื่นภายในระบบของออสเตรเลียใช้คำแปล NAATI ได้เลย แต่ถ้าเอกสารต้องใช้กับหน่วยงานไทยหรือประเทศที่สามด้วย อาจต้องเดินสายนิติกรณ์กรมการกงสุลเพิ่ม" },
      { q: "ไฟล์ดิจิทัลใช้ได้หรือไม่", a: "หน่วยงานออสเตรเลียส่วนใหญ่รับไฟล์ PDF ที่มีตราประทับและลายมือชื่อดิจิทัล แต่บางหน่วยงานยังขอฉบับพิมพ์ ควรตรวจข้อกำหนดของแบบฟอร์มที่ยื่น" },
      { q: "แปลได้กี่ภาษา", a: "คู่ภาษาที่ NAATI รับรองมีจำกัดตามผู้ปฏิบัติงานแต่ละราย งานไทย–อังกฤษเป็นคู่ภาษาที่พบมากที่สุด หากเป็นคู่ภาษาอื่นควรสอบถามก่อนส่งงาน" },
      { q: "เอกสารเก่ามากแปลได้ไหม", a: "แปลได้หากอ่านออกครบถ้วน หากบางส่วนเลือนลาง นักแปลจะระบุหมายเหตุว่าอ่านไม่ได้ตามมาตรฐาน ซึ่งอาจทำให้ปลายทางขอเอกสารฉบับคัดใหม่" },
      { q: "มีบริการด่วนไหม", a: "มี ขึ้นกับคิวงานและจำนวนหน้า เราแจ้งกรอบเวลาที่ทำได้จริงก่อนยืนยันรับงานทุกครั้ง" },
    ],
    feeKeys: [],
    related: [
      { label: "บริการแปล NAATI Certified", href: "/naati" },
      { label: "ตรวจสอบประวัติอาชญากรรมเพื่อยื่นต่างประเทศ", href: "/pcc" },
      { label: "บริการวีซ่าและติดตามครอบครัว", href: "/visa" },
    ],
    keywords: ["แนะนำนักแปล NAATI ออสเตรเลีย", "แปล NAATI ยื่น Home Affairs", "แนะนำแปลใบขับขี่ NAATI"],
  },
  {
    slug: "รับรองหนังสือมอบอำนาจ",
    cluster: "legalize",
    h1: "แนะนำบริการรับรองหนังสือมอบอำนาจ (Power of Attorney) ใช้ต่างประเทศ",
    title: "แนะนำบริการรับรองหนังสือมอบอำนาจใช้ต่างประเทศ",
    desc: "เลือกผู้ให้บริการรับรองหนังสือมอบอำนาจอย่างไรให้ปลายทางรับ ครบสาย Notary — นิติกรณ์ — สถานทูต พร้อมเอกสารและกรอบเวลา ปรึกษาทีมทนายได้",
    answer:
      "หนังสือมอบอำนาจที่จะใช้ต่างประเทศต้องลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อ แล้วจึงนำไปรับรองนิติกรณ์ที่กรมการกงสุล และรับรองสถานทูตปลายทางเมื่อประเทศนั้นกำหนด ควรเลือกผู้ให้บริการที่ตรวจถ้อยคำในหนังสือมอบอำนาจให้ตรงกับสิ่งที่ปลายทางร้องขอก่อนลงนาม เพราะแก้ไขหลังรับรองแล้วต้องเริ่มใหม่ทั้งสาย",
    who: "ผู้มอบอำนาจซื้อขายทรัพย์สินต่างประเทศ ผู้ดำเนินคดีแทน และนิติบุคคลที่มอบอำนาจกรรมการ",
    criteria: [
      "ทนายผู้รับรองมีใบอนุญาตและหมายเลขทะเบียนที่ตรวจสอบได้กับสภาทนายความ",
      "ตรวจร่างถ้อยคำก่อนนัดลงนาม ไม่ใช่รับรองลายมือชื่ออย่างเดียว",
      "เดินเรื่องนิติกรณ์และสถานทูตต่อได้ในที่เดียว",
      "ระบุกรอบเวลาแต่ละขั้นเป็นวันทำการอย่างชัดเจน",
    ],
    docs: [
      "ร่างหนังสือมอบอำนาจจากปลายทาง (ถ้ามี)",
      "หนังสือเดินทางหรือบัตรประชาชนตัวจริงของผู้มอบอำนาจ",
      "หนังสือรับรองบริษัทฉบับล่าสุด กรณีมอบอำนาจในนามนิติบุคคล",
      "รายละเอียดผู้รับมอบอำนาจและขอบเขตอำนาจที่ต้องการ",
    ],
    steps: [
      { t: "ตรวจร่างเอกสาร", d: "ทนายตรวจขอบเขตอำนาจและถ้อยคำให้ตรงกับที่ปลายทางกำหนด" },
      { t: "ลงนามต่อหน้าทนาย", d: "ผู้มอบอำนาจแสดงตัวตนและลงนามต่อหน้าทนายผู้ทำคำรับรอง" },
      { t: "นิติกรณ์กรมการกงสุล", d: "รับรองลายมือชื่อทนายที่กองสัญชาติและนิติกรณ์" },
      { t: "รับรองสถานทูตปลายทาง", d: "เฉพาะประเทศที่กำหนด ก่อนส่งเอกสารออกไปใช้จริง" },
    ],
    timeline: "นัดลงนามภายในวันเดียวเมื่อเอกสารพร้อม · นิติกรณ์ 2–3 วันทำการ · สถานทูตแล้วแต่ระเบียบแต่ละแห่ง",
    pitfalls: [
      "ลงนามมาก่อนแล้วขอให้รับรองย้อนหลัง ซึ่งทำไม่ได้",
      "ขอบเขตอำนาจกว้างหรือแคบเกินกว่าที่ปลายทางต้องการ",
      "ใช้หนังสือรับรองบริษัทเก่าเกินกำหนดที่ปลายทางรับ",
    ],
    faqs: [
      { q: "ต้องมาลงนามด้วยตนเองไหม", a: "ต้องลงนามต่อหน้าทนายผู้ทำคำรับรอง เพราะสาระสำคัญคือการยืนยันว่าลายมือชื่อเป็นของผู้มอบอำนาจจริง หากอยู่ต่างประเทศสามารถใช้บริการที่สถานเอกอัครราชทูตไทยแทนได้" },
      { q: "ภาษาไทยหรืออังกฤษดีกว่ากัน", a: "ขึ้นกับปลายทาง หลายประเทศต้องการฉบับภาษาอังกฤษหรือภาษาราชการของตน หากทำสองภาษาควรจัดเป็นคอลัมน์คู่ในฉบับเดียวเพื่อลดปัญหาการรับรองซ้ำซ้อน" },
      { q: "ประเทศไทยใช้ Apostille แทนได้แล้วหรือยัง", a: "ยังไม่ได้ อนุสัญญากรุงเฮกจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570 ก่อนหน้านั้นยังต้องใช้สายนิติกรณ์กรมการกงสุลและรับรองสถานทูตตามเดิม" },
      { q: "นิติบุคคลมอบอำนาจต้องใช้อะไรเพิ่ม", a: "ต้องมีหนังสือรับรองบริษัทฉบับล่าสุด ตราประทับตามข้อบังคับ และกรรมการผู้มีอำนาจลงนามมาแสดงตัว หากกรรมการอยู่ต่างประเทศต้องดำเนินการผ่านสถานทูตไทย" },
      { q: "รับรองแล้วมีอายุใช้งานนานแค่ไหน", a: "ตัวการรับรองไม่มีวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางหลายแห่งกำหนดให้เอกสารออกไม่เกิน 3–6 เดือน จึงควรตรวจเงื่อนไขก่อนดำเนินการ" },
      { q: "แก้ไขข้อความหลังรับรองได้ไหม", a: "ไม่ได้ ต้องจัดทำฉบับใหม่และเดินสายรับรองใหม่ทั้งหมด จึงควรให้ทนายตรวจร่างให้เรียบร้อยก่อนนัดลงนาม" },
    ],
    feeKeys: ["mfaLegalisation", "embassyLegalisation"],
    related: [
      { label: "บริการ Notary Public / ทนายผู้ทำคำรับรอง", href: "/notary" },
      { label: "รับรองเอกสารกงสุล (นิติกรณ์)", href: "/lgz" },
      { label: "งานเอกสารนิติบุคคล", href: "/corporate" },
    ],
    keywords: [
      "แนะนำรับรองหนังสือมอบอำนาจ",
      "แนะนำ notary public หนังสือมอบอำนาจ",
      "รับรองเอกสารสถานทูต",
    ],
  },
  {
    slug: "รับรองเอกสารสถานทูต",
    cluster: "legalize",
    h1: "แนะนำบริการรับรองเอกสารสถานทูตในประเทศไทย",
    title: "แนะนำบริการรับรองเอกสารสถานทูต เลือกอย่างไร",
    desc: "ลำดับการรับรองที่ถูกต้อง ตั้งแต่แปล นิติกรณ์กรมการกงสุล จนถึงสถานทูตปลายทาง พร้อมข้อผิดพลาดที่ทำให้เสียรอบ ปรึกษาทีมทนายก่อนยื่น",
    answer:
      "สถานทูตส่วนใหญ่ในไทยจะรับรองเอกสารได้ก็ต่อเมื่อเอกสารผ่านการรับรองนิติกรณ์จากกรมการกงสุล กระทรวงการต่างประเทศมาก่อนแล้ว ลำดับที่ถูกต้องคือ ต้นฉบับหรือคำรับรองของทนาย ตามด้วยคำแปล จากนั้นนิติกรณ์ แล้วจึงยื่นสถานทูต การข้ามขั้นตอนใดขั้นตอนหนึ่งเป็นสาเหตุที่พบบ่อยที่สุดของการถูกตีกลับ",
    who: "ผู้ยื่นเอกสารกับสถานทูตต่างประเทศในไทย ทั้งงานสมรส งานธุรกิจ และงานการศึกษา",
    criteria: [
      "รู้ระเบียบเฉพาะของแต่ละสถานทูต เช่น การนัดคิวและภาษาที่ต้องใช้",
      "จัดชุดเอกสารครบตามรายการก่อนยื่น ไม่ยื่นแบบทยอย",
      "แจ้งกรอบเวลาตามระเบียบจริง ไม่รับปากผลลัพธ์แทนหน่วยงาน",
      "มีระบบติดตามสถานะและแจ้งผู้ว่าจ้างทุกขั้น",
    ],
    docs: [
      "ต้นฉบับเอกสารหรือฉบับคัดรับรองจากหน่วยงานผู้ออก",
      "คำแปลภาษาที่สถานทูตกำหนด",
      "สำเนาหนังสือเดินทางผู้ถือเอกสาร",
      "หนังสือมอบอำนาจกรณีให้ดำเนินการแทน",
    ],
    steps: [
      { t: "ตรวจข้อกำหนดของสถานทูต", d: "แต่ละแห่งกำหนดภาษา จำนวนชุด และรูปแบบคำแปลต่างกัน" },
      { t: "จัดเตรียมต้นฉบับ", d: "คัดฉบับใหม่จากหน่วยงานผู้ออกเมื่อเอกสารเก่าหรือมีรอยชำรุด" },
      { t: "แปลและรับรองคำแปล", d: "ใช้ผู้แปลที่ลงนามรับผิดชอบและรูปแบบตรงต้นฉบับ" },
      { t: "ยื่นนิติกรณ์กรมการกงสุล", d: "ขั้นบังคับก่อนยื่นสถานทูตเกือบทุกกรณี" },
      { t: "ยื่นสถานทูตปลายทาง", d: "ตามคิวและระเบียบของสถานทูตนั้น" },
    ],
    timeline: "นิติกรณ์ปกติ 2–3 วันทำการ · การรับรองสถานทูตแตกต่างกันมาก ตั้งแต่ 1 วันถึงหลายสัปดาห์ตามระเบียบของแต่ละแห่ง",
    pitfalls: [
      "ยื่นสถานทูตก่อนผ่านนิติกรณ์",
      "แปลผิดภาษาที่สถานทูตกำหนด",
      "เอกสารต้นฉบับออกเกินกำหนดอายุที่ปลายทางรับ",
      "ไม่ได้นัดคิวล่วงหน้าในสถานทูตที่บังคับนัดหมาย",
    ],
    faqs: [
      { q: "ทุกประเทศต้องรับรองสถานทูตไหม", a: "ไม่ทุกประเทศ ขึ้นกับข้อกำหนดของหน่วยงานปลายทาง บางประเทศรับเอกสารที่ผ่านนิติกรณ์กรมการกงสุลก็เพียงพอ ควรตรวจกับหน่วยงานผู้รับเอกสารก่อนเริ่มเดินเรื่อง" },
      { q: "เมื่อ Apostille มีผลกับไทยแล้วจะเปลี่ยนอย่างไร", a: "เมื่ออนุสัญญากรุงเฮกมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2570 เอกสารที่ใช้ในประเทศภาคีจะใช้ตรา Apostille แทนการรับรองสถานทูตได้ แต่ประเทศที่ไม่ใช่ภาคียังต้องเดินสายเดิม" },
      { q: "ยื่นแทนได้ไหม", a: "ได้ในกรณีส่วนใหญ่ โดยใช้หนังสือมอบอำนาจและสำเนาเอกสารแสดงตน ยกเว้นบางสถานทูตที่กำหนดให้เจ้าของเอกสารมาด้วยตนเอง" },
      { q: "เอกสารภาษาที่สามต้องทำอย่างไร", a: "โดยทั่วไปต้องแปลเป็นภาษาอังกฤษหรือภาษาราชการของประเทศปลายทาง และคำแปลต้องผ่านการรับรองตามลำดับเช่นเดียวกับต้นฉบับ" },
      { q: "ค่าใช้จ่ายมีอะไรบ้าง", a: "ประกอบด้วยค่าธรรมเนียมราชการของกรมการกงสุล ค่าธรรมเนียมของสถานทูตแต่ละแห่ง ค่าแปล และค่าบริการเดินเรื่อง โดยค่าธรรมเนียมราชการอ้างอิงตามประกาศของหน่วยงานที่แสดงไว้ในหน้านี้" },
      { q: "ถ้าสถานทูตปฏิเสธจะทำอย่างไร", a: "ขอเหตุผลเป็นลายลักษณ์อักษรหรือบันทึกจุดที่ทักท้วง แล้วแก้เฉพาะจุดนั้นก่อนยื่นใหม่ ส่วนใหญ่เป็นเรื่องรูปแบบคำแปลหรือเอกสารประกอบไม่ครบ" },
    ],
    feeKeys: ["mfaLegalisation", "embassyLegalisation"],
    related: [
      { label: "รับรองเอกสารกงสุล (นิติกรณ์)", href: "/lgz" },
      { label: "สถานะ Apostille ของประเทศไทย", href: "/apostille" },
      { label: "บริการ Notary Public", href: "/notary" },
    ],
    keywords: [
      "แนะนำรับรองเอกสารสถานทูต",
      "แนะนำบริการรับรองกงสุล",
      "รับรองนิติกรณ์กรมการกงสุล",
    ],
  },
  {
    slug: "วีซ่าแต่งงานและติดตามครอบครัว",
    cluster: "visa",
    h1: "แนะนำบริการเตรียมเอกสารวีซ่าแต่งงานและติดตามครอบครัว",
    title: "แนะนำบริการวีซ่าแต่งงาน เลือกผู้ให้บริการอย่างไร",
    desc: "เกณฑ์เลือกผู้ช่วยเตรียมเอกสารวีซ่าแต่งงานและติดตามครอบครัว พร้อมชุดเอกสาร ขั้นตอน และข้อควรระวังเรื่องคำโฆษณาเกินจริง ปรึกษาทีมทนายก่อนยื่น",
    answer:
      "ผู้ให้บริการที่ดีจะช่วยจัดชุดเอกสารให้ครบและสอดคล้องกัน ทั้งเอกสารความสัมพันธ์ เอกสารการเงิน และเอกสารทะเบียนราษฎรที่ผ่านการแปลและรับรองอย่างถูกต้อง แต่จะไม่ให้คำมั่นเรื่องผลอนุมัติ เพราะการพิจารณาเป็นดุลพินิจของสถานทูตหรือหน่วยงานตรวจคนเข้าเมืองปลายทางเท่านั้น",
    who: "คู่สมรสชาวไทยและชาวต่างชาติ ผู้ยื่นวีซ่าคู่หมั้น และผู้ติดตามครอบครัวไปพำนักต่างประเทศ",
    criteria: [
      "ไม่ใช้ถ้อยคำให้คำมั่นเรื่องผลอนุมัติ หรืออ้างเส้นสายกับหน่วยงาน",
      "มีเช็กลิสต์เอกสารเฉพาะประเทศปลายทาง ไม่ใช่แบบฟอร์มกลาง",
      "รับผิดชอบงานแปลและรับรองเอกสารในสายเดียวกัน",
      "มีทนายตรวจความสอดคล้องของเอกสารการเงินและความสัมพันธ์",
    ],
    docs: [
      "ทะเบียนสมรสหรือเอกสารการหมั้น พร้อมคำแปลรับรอง",
      "ใบรับรองโสด สูติบัตร ทะเบียนบ้าน ตามที่ปลายทางกำหนด",
      "หลักฐานความสัมพันธ์ เช่น ภาพถ่าย ประวัติการเดินทาง บทสนทนา",
      "หลักฐานการเงินและที่พักอาศัยของผู้สนับสนุน",
    ],
    steps: [
      { t: "ประเมินกรณี", d: "ทนายตรวจสถานะทางทะเบียนและประวัติการยื่นก่อนหน้า" },
      { t: "จัดเช็กลิสต์ตามประเทศ", d: "อ้างอิงข้อกำหนดของสถานทูตหรือหน่วยงานตรวจคนเข้าเมืองปลายทาง" },
      { t: "แปลและรับรองเอกสาร", d: "เดินสายนิติกรณ์และสถานทูตตามที่จำเป็น" },
      { t: "ทวนความสอดคล้อง", d: "ตรวจวันที่ ชื่อ และตัวเลขทางการเงินให้ตรงกันทุกฉบับ" },
      { t: "ยื่นและติดตามผล", d: "จัดชุดยื่นและเตรียมประเด็นสัมภาษณ์ตามแนวคำถามที่พบบ่อย" },
    ],
    timeline: "เตรียมเอกสารทั่วไป 1–3 สัปดาห์ขึ้นกับจำนวนเอกสารที่ต้องคัดใหม่ · ระยะพิจารณาเป็นไปตามกรอบของสถานทูตหรือหน่วยงานปลายทาง",
    pitfalls: [
      "เอกสารการเงินไม่สอดคล้องกับที่แจ้งในแบบฟอร์ม",
      "คำแปลไม่ได้รับรองตามลำดับที่ปลายทางกำหนด",
      "ใช้เอกสารทะเบียนราษฎรที่คัดไว้นานเกินกำหนด",
      "หลงเชื่อผู้ให้บริการที่ให้คำมั่นเรื่องผลอนุมัติ",
    ],
    faqs: [
      { q: "มีบริการที่การันตีผลวีซ่าหรือไม่", a: "ไม่มี และผู้ให้บริการรายใดที่ให้คำมั่นเรื่องผลควรหลีกเลี่ยง เพราะการอนุมัติเป็นดุลพินิจของหน่วยงานปลายทาง สิ่งที่ทำได้คือจัดเอกสารให้ครบถ้วน ถูกต้อง และสอดคล้องกัน" },
      { q: "ต้องจดทะเบียนสมรสในไทยก่อนไหม", a: "ขึ้นกับประเภทวีซ่า วีซ่าคู่สมรสต้องมีทะเบียนสมรสที่ปลายทางรับรองความถูกต้อง ส่วนวีซ่าคู่หมั้นใช้หลักฐานความสัมพันธ์และแผนการสมรสภายหลัง" },
      { q: "เอกสารไทยต้องรับรองถึงขั้นไหน", a: "ปกติต้องแปลและผ่านนิติกรณ์กรมการกงสุล และหลายประเทศต้องรับรองสถานทูตปลายทางเพิ่ม จนกว่าอนุสัญญากรุงเฮกจะมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2570" },
      { q: "เคยถูกปฏิเสธมาก่อนยื่นใหม่ได้ไหม", a: "ยื่นใหม่ได้ แต่ควรวิเคราะห์เหตุผลการปฏิเสธเดิมและเพิ่มหลักฐานในประเด็นที่ถูกทักท้วง การยื่นซ้ำด้วยชุดเอกสารเดิมมักได้ผลเหมือนเดิม" },
      { q: "ชาวต่างชาติในไทยใช้บริการได้ไหม", a: "ได้ เรารองรับเอกสารทั้งขาเข้าและขาออก รวมถึงการแปลเอกสารต่างประเทศเพื่อใช้ในไทยและการรับรองที่เกี่ยวข้อง" },
      { q: "ต้องมาที่สำนักงานไหม", a: "ไม่จำเป็นทุกขั้นตอน ส่วนใหญ่ประสานงานออนไลน์และส่งเอกสารทางขนส่งได้ ยกเว้นขั้นตอนที่ต้องลงนามต่อหน้าทนายหรือแสดงตนต่อหน่วยงาน" },
    ],
    feeKeys: ["mfaLegalisation", "embassyLegalisation", "marriageRegistration"],
    related: [
      { label: "บริการวีซ่าและตรวจคนเข้าเมือง", href: "/visa" },
      { label: "จดทะเบียนสมรสไทย–ต่างชาติ", href: "/marriage" },
      { label: "แปลและรับรองเอกสารทะเบียนราษฎร", href: "/civil-registration" },
    ],
    keywords: [
      "แนะนำบริการทำวีซ่าแต่งงาน",
      "แนะนำเอเจนซี่วีซ่าติดตามครอบครัว",
      "แนะนำแปลเอกสารยื่นวีซ่าคู่สมรส",
    ],
  },
  {
    slug: "work-permit-และวีซ่าธุรกิจ",
    cluster: "visa",
    h1: "แนะนำบริการ Work Permit และวีซ่าธุรกิจสำหรับชาวต่างชาติในไทย",
    title: "แนะนำบริการ Work Permit และวีซ่าธุรกิจในไทย",
    desc: "เกณฑ์เลือกผู้ให้บริการยื่นใบอนุญาตทำงานและวีซ่า Non-B พร้อมเอกสารฝั่งบริษัทและฝั่งบุคคล ขั้นตอน และข้อควรระวัง ปรึกษาทีมทนายก่อนยื่น",
    answer:
      "งาน Work Permit และวีซ่าธุรกิจต้องเตรียมเอกสารสองฝั่งพร้อมกัน คือฝั่งนายจ้าง เช่น หนังสือรับรองบริษัท งบการเงิน และรายชื่อลูกจ้าง กับฝั่งลูกจ้าง เช่น วุฒิการศึกษาและใบรับรองประสบการณ์ที่ผ่านการแปลและรับรอง ผู้ให้บริการที่ดีจะตรวจคุณสมบัติทั้งสองฝั่งก่อนเริ่มยื่น เพื่อลดการถูกขอเอกสารเพิ่มกลางทาง",
    who: "บริษัทที่จ้างชาวต่างชาติ ผู้บริหารต่างชาติ และผู้ถือวีซ่า Non-B ที่ต้องต่ออายุ",
    criteria: [
      "ตรวจคุณสมบัติของบริษัทก่อนรับงาน เช่น ทุนจดทะเบียนและอัตราส่วนลูกจ้างไทย",
      "แจ้งรายการเอกสารทั้งสองฝั่งตั้งแต่ต้น ไม่ทยอยขอทีละใบ",
      "ดูแลการต่ออายุและรายงานตัว 90 วันต่อเนื่อง",
      "อ้างอิงค่าธรรมเนียมราชการตามประกาศ ไม่ผูกกับคำโฆษณาเกินจริง",
    ],
    docs: [
      "หนังสือรับรองบริษัทและบัญชีรายชื่อผู้ถือหุ้นฉบับล่าสุด",
      "งบการเงินปีล่าสุดและแบบยื่นภาษีที่เกี่ยวข้อง",
      "วุฒิการศึกษาและใบรับรองการทำงานของลูกจ้างต่างชาติ พร้อมคำแปลรับรอง",
      "หนังสือเดินทางและวีซ่าปัจจุบันของลูกจ้าง",
    ],
    steps: [
      { t: "ตรวจคุณสมบัตินายจ้าง", d: "ทุนจดทะเบียน จำนวนลูกจ้างไทย และประเภทธุรกิจที่ขอได้" },
      { t: "จัดเอกสารฝั่งลูกจ้าง", d: "แปลและรับรองวุฒิการศึกษาและใบรับรองประสบการณ์จากต่างประเทศ" },
      { t: "ยื่นขอวีซ่า Non-B", d: "ยื่นที่สถานทูตไทยหรือเปลี่ยนประเภทการตรวจลงตราตามกรณี" },
      { t: "ยื่นใบอนุญาตทำงาน", d: "ยื่นต่อกรมการจัดหางานตามพื้นที่ที่สถานประกอบการตั้งอยู่" },
      { t: "ดูแลหลังได้รับอนุญาต", d: "ต่ออายุ รายงานตัว 90 วัน และแจ้งเปลี่ยนแปลงข้อมูลตามกำหนด" },
    ],
    timeline: "เตรียมเอกสาร 1–2 สัปดาห์ · ระยะพิจารณาเป็นไปตามกรอบของกรมการจัดหางานและสำนักงานตรวจคนเข้าเมืองในแต่ละพื้นที่",
    pitfalls: [
      "ทุนจดทะเบียนหรืออัตราส่วนลูกจ้างไทยไม่ถึงเกณฑ์",
      "วุฒิการศึกษาต่างประเทศยังไม่ผ่านการรับรองตามลำดับ",
      "ทำงานก่อนได้รับใบอนุญาต ซึ่งมีโทษตามกฎหมาย",
      "ลืมรายงานตัว 90 วันหรือแจ้งย้ายที่พัก",
    ],
    faqs: [
      { q: "ต้องมีวีซ่า Non-B ก่อนขอใบอนุญาตทำงานไหม", a: "โดยหลักต้องมีการตรวจลงตราประเภทที่ถูกต้องก่อนยื่นขอใบอนุญาตทำงาน การเข้ามาด้วยวีซ่าท่องเที่ยวแล้วทำงานทันทีเป็นความเสี่ยงทางกฎหมายที่ควรหลีกเลี่ยง" },
      { q: "บริษัทเปิดใหม่ขอได้ไหม", a: "ขอได้หากคุณสมบัติครบตามเกณฑ์ที่หน่วยงานกำหนด เช่น ทุนจดทะเบียนและเอกสารทางบัญชี แต่บริษัทที่ยังไม่มีงบการเงินอาจถูกขอเอกสารประกอบเพิ่มเติม" },
      { q: "วุฒิการศึกษาต่างประเทศต้องรับรองอย่างไร", a: "ต้องผ่านการรับรองจากหน่วยงานในประเทศที่ออกเอกสารและสถานทูตไทยในประเทศนั้น จากนั้นแปลเป็นภาษาไทยหรืออังกฤษและรับรองคำแปลตามที่หน่วยงานไทยกำหนด" },
      { q: "เปลี่ยนนายจ้างต้องทำอย่างไร", a: "ต้องดำเนินการยกเลิกใบอนุญาตเดิมและยื่นขอใหม่ภายใต้ผู้จ้างรายใหม่ ไม่สามารถโอนใบอนุญาตข้ามนายจ้างได้โดยอัตโนมัติ" },
      { q: "ค่าธรรมเนียมเท่าไร", a: "ค่าธรรมเนียมราชการเป็นไปตามประกาศของกรมการจัดหางานและสำนักงานตรวจคนเข้าเมือง ดูรายละเอียดที่บล็อกค่าธรรมเนียมทางการในหน้านี้ ส่วนค่าบริการประเมินตามความซับซ้อนของเคส" },
      { q: "ดูแลต่ออายุให้ด้วยหรือไม่", a: "ดูแลได้ตลอดสัญญา ทั้งการต่ออายุใบอนุญาตทำงาน การขยายระยะเวลาพำนัก และการรายงานตัวตามกำหนด" },
    ],
    feeKeys: ["workPermit", "immigrationExtension", "reEntryPermit"],
    related: [
      { label: "บริการวีซ่าและใบอนุญาตทำงาน", href: "/visa" },
      { label: "จดทะเบียนบริษัทและงานนิติบุคคล", href: "/corporate" },
      { label: "บริการพำนักระยะยาว", href: "/longstay" },
    ],
    keywords: [
      "แนะนำบริการทำ Work Permit",
      "แนะนำบริการวีซ่าธุรกิจ Non-B",
      "แนะนำที่ปรึกษาวีซ่าชาวต่างชาติในไทย",
    ],
  },
  {
    slug: "หนังสือรับรองความประพฤติ",
    cluster: "pcc",
    h1: "แนะนำบริการขอหนังสือรับรองความประพฤติ (Police Clearance)",
    title: "แนะนำบริการขอหนังสือรับรองความประพฤติ",
    desc: "เลือกผู้ช่วยขอ Police Clearance อย่างไรให้ครบสายถึงการรับรองปลายทาง พร้อมเอกสาร ขั้นตอน และกรอบเวลา ปรึกษาทีมงานก่อนเริ่มเรื่อง",
    answer:
      "หนังสือรับรองความประพฤติออกโดยศูนย์บริการออกหนังสือรับรองความประพฤติ สำนักงานตำรวจแห่งชาติ ผู้ยื่นต้องพิมพ์ลายนิ้วมือตามแบบที่กำหนดและแนบเอกสารแสดงเหตุผลการขอ เช่น หนังสือเรียกจากหน่วยงานปลายทาง หากต้องนำไปใช้ต่างประเทศ ให้เผื่อเวลาสำหรับนิติกรณ์กรมการกงสุลและการรับรองสถานทูตเพิ่มด้วย",
    who: "ผู้ยื่นวีซ่าถาวร ผู้สมัครงานต่างประเทศ ผู้ขอสัญชาติ และผู้เทียบวิชาชีพ",
    criteria: [
      "ช่วยจัดพิมพ์ลายนิ้วมือให้ถูกแบบฟอร์มของหน่วยงานปลายทาง",
      "เดินเรื่องแปลและนิติกรณ์ต่อได้ในสายเดียว",
      "แจ้งกรอบเวลาตามระเบียบจริงของสำนักงานตำรวจแห่งชาติ",
      "ไม่กล่าวอ้างว่าเร่งผลหรือแทรกคิวได้",
    ],
    docs: [
      "หนังสือเดินทางและบัตรประชาชนตัวจริงพร้อมสำเนา",
      "แบบพิมพ์ลายนิ้วมือตามที่กำหนด",
      "เอกสารแสดงเหตุผลการขอ เช่น หนังสือเรียกหรือแบบฟอร์มจากหน่วยงานปลายทาง",
      "หนังสือมอบอำนาจกรณีให้ดำเนินการแทน",
    ],
    steps: [
      { t: "เตรียมเอกสารและลายนิ้วมือ", d: "พิมพ์ลายนิ้วมือที่สถานีตำรวจหรือหน่วยงานที่รับดำเนินการ" },
      { t: "ยื่นคำขอ", d: "ยื่นต่อศูนย์บริการออกหนังสือรับรองความประพฤติพร้อมเอกสารประกอบครบชุด" },
      { t: "รอผลตรวจสอบ", d: "ระยะเวลาเป็นไปตามกรอบที่หน่วยงานกำหนด" },
      { t: "แปลและรับรองต่อ", d: "กรณีใช้ต่างประเทศ ให้ยื่นนิติกรณ์และรับรองสถานทูตตามที่ปลายทางกำหนด" },
    ],
    timeline: "ระยะพิจารณาเป็นไปตามประกาศของสำนักงานตำรวจแห่งชาติ · ขั้นแปลและนิติกรณ์เพิ่มอีก 3–5 วันทำการโดยประมาณ",
    pitfalls: [
      "พิมพ์ลายนิ้วมือไม่ชัดหรือใช้แบบฟอร์มผิด",
      "ไม่มีเอกสารแสดงเหตุผลการขอตามที่หน่วยงานกำหนด",
      "ชื่อในหนังสือรับรองสะกดไม่ตรงหนังสือเดินทาง",
      "ไม่เผื่อเวลาสำหรับขั้นรับรองต่อในต่างประเทศ",
    ],
    faqs: [
      { q: "ขอจากต่างประเทศได้ไหม", a: "ได้ โดยมอบอำนาจให้ผู้แทนในไทยดำเนินการ พร้อมส่งแบบพิมพ์ลายนิ้วมือที่จัดทำจากหน่วยงานตำรวจหรือสถานทูตไทยในประเทศที่พำนัก" },
      { q: "หนังสือรับรองมีอายุเท่าไร", a: "ตัวเอกสารไม่ได้ระบุวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางส่วนใหญ่กำหนดให้ออกไม่เกิน 3–6 เดือน จึงควรตรวจเงื่อนไขก่อนยื่นขอ" },
      { q: "เคยมีคดีจะขอได้ไหม", a: "ขอได้ ผลจะแสดงข้อมูลตามที่ปรากฏในฐานข้อมูล การมีประวัติไม่ได้แปลว่าจะถูกปฏิเสธเสมอไป ขึ้นกับเกณฑ์ของหน่วยงานปลายทางแต่ละแห่ง" },
      { q: "ต้องแปลเป็นภาษาอังกฤษไหม", a: "หน่วยงานออกเอกสารสามารถออกฉบับภาษาอังกฤษได้ในหลายกรณี หากปลายทางต้องการภาษาอื่นต้องแปลและรับรองคำแปลเพิ่ม" },
      { q: "ใช้กับวีซ่าออสเตรเลียต้องทำอะไรเพิ่ม", a: "โดยทั่วไปยื่นฉบับภาษาอังกฤษได้ หากใช้ฉบับภาษาไทยต้องมีคำแปลโดยนักแปลที่ NAATI รับรอง" },
      { q: "มีบริการเร่งด่วนไหม", a: "ระยะเวลาขึ้นกับหน่วยงานผู้ออกเอกสาร เราช่วยลดเวลาในส่วนของการจัดเอกสารและการเดินเรื่องรับรองต่อ แต่ไม่สามารถเร่งการตรวจสอบของหน่วยงานได้" },
    ],
    feeKeys: ["policeClearance", "mfaLegalisation"],
    related: [
      { label: "บริการตรวจสอบประวัติอาชญากรรม", href: "/pcc" },
      { label: "รับรองเอกสารกงสุล", href: "/lgz" },
      { label: "บริการแปล NAATI", href: "/naati" },
    ],
    keywords: [
      "แนะนำบริการขอหนังสือรับรองความประพฤติ",
      "แนะนำบริการตรวจประวัติอาชญากรรม",
      "police clearance certificate thailand",
    ],
  },
  {
    slug: "เอกสารบริษัทใช้ต่างประเทศ",
    cluster: "corporate",
    h1: "แนะนำบริการแปลและรับรองเอกสารบริษัทเพื่อใช้ต่างประเทศ",
    title: "แนะนำบริการรับรองเอกสารบริษัทใช้ต่างประเทศ",
    desc: "หนังสือรับรองบริษัท งบการเงิน และสัญญา ต้องแปลและรับรองอย่างไรจึงใช้ต่างประเทศได้ พร้อมขั้นตอนและข้อควรระวัง ปรึกษาทีมทนายก่อนยื่น",
    answer:
      "เอกสารนิติบุคคลที่จะใช้ต่างประเทศต้องเริ่มจากฉบับคัดรับรองล่าสุดจากกรมพัฒนาธุรกิจการค้า แปลโดยผู้แปลที่ลงนามรับผิดชอบ แล้วผ่านนิติกรณ์กรมการกงสุล ก่อนรับรองสถานทูตปลายทางเมื่อจำเป็น เอกสารที่คัดไว้นานเกินกำหนดของปลายทางเป็นสาเหตุการถูกตีกลับที่พบบ่อยที่สุดในงานประเภทนี้",
    who: "บริษัทที่ประมูลงานต่างประเทศ ตั้งบริษัทลูก เปิดบัญชีธนาคารต่างประเทศ และจดทะเบียนเครื่องหมายการค้า",
    criteria: [
      "ตรวจอายุเอกสารที่ปลายทางรับก่อนคัดฉบับใหม่",
      "แปลชื่อบริษัทและวัตถุประสงค์ให้ตรงกับที่จดทะเบียนไว้",
      "จัดชุดเอกสารหลายฉบับพร้อมกันเพื่อลดรอบการเดินเรื่อง",
      "มีทนายตรวจเอกสารมอบอำนาจกรรมการควบคู่",
    ],
    docs: [
      "หนังสือรับรองบริษัทฉบับคัดรับรองล่าสุด",
      "บัญชีรายชื่อผู้ถือหุ้น (บอจ.5)",
      "งบการเงินและแบบนำส่งที่เกี่ยวข้อง",
      "หนังสือมอบอำนาจกรรมการผู้มีอำนาจลงนาม",
    ],
    steps: [
      { t: "คัดเอกสารใหม่", d: "ขอฉบับคัดรับรองจากกรมพัฒนาธุรกิจการค้าให้เป็นปัจจุบัน" },
      { t: "แปลโดยผู้รับผิดชอบ", d: "แปลตรงตามชื่อและวัตถุประสงค์ที่จดทะเบียน" },
      { t: "รับรองลายมือชื่อกรรมการ", d: "ลงนามต่อหน้าทนายผู้ทำคำรับรองเมื่อปลายทางกำหนด" },
      { t: "นิติกรณ์และสถานทูต", d: "ยื่นกรมการกงสุลแล้วรับรองสถานทูตปลายทางตามข้อกำหนด" },
    ],
    timeline: "คัดเอกสาร 1 วันทำการ · แปลและตรวจ 1–3 วันทำการ · นิติกรณ์ 2–3 วันทำการ · สถานทูตตามระเบียบแต่ละแห่ง",
    pitfalls: [
      "ใช้หนังสือรับรองที่คัดไว้เกิน 3 เดือน",
      "แปลชื่อบริษัทไม่ตรงกับที่จดทะเบียนภาษาอังกฤษไว้แล้ว",
      "ลืมรับรองหน้าเอกสารแนบทุกหน้าตามที่ปลายทางกำหนด",
    ],
    faqs: [
      { q: "หนังสือรับรองบริษัทมีอายุกี่เดือน", a: "ตัวเอกสารไม่ระบุอายุ แต่หน่วยงานและธนาคารต่างประเทศส่วนใหญ่กำหนดให้คัดไม่เกิน 3–6 เดือน จึงควรคัดใหม่ก่อนเริ่มเดินเรื่องทุกครั้ง" },
      { q: "งบการเงินต้องรับรองด้วยไหม", a: "ขึ้นกับผู้รับเอกสาร บางกรณีต้องมีผู้สอบบัญชีรับอนุญาตรับรองและผ่านนิติกรณ์เช่นเดียวกับเอกสารทะเบียนนิติบุคคล" },
      { q: "กรรมการอยู่ต่างประเทศทำอย่างไร", a: "ลงนามต่อหน้าเจ้าหน้าที่ที่สถานเอกอัครราชทูตไทยในประเทศที่พำนัก หรือใช้ Notary Public ของประเทศนั้นแล้วนำมารับรองต่อในไทยตามลำดับที่กำหนด" },
      { q: "ต้องแปลทั้งชุดหรือเฉพาะหน้าแรก", a: "ต้องแปลครบทุกหน้าที่จะยื่น รวมถึงตราประทับและหมายเหตุ การแปลเฉพาะหน้าแรกมักถูกตีกลับที่ขั้นตอนนิติกรณ์" },
      { q: "รองรับภาษาอื่นนอกจากอังกฤษไหม", a: "รองรับภาษาหลักที่ใช้ในการค้าระหว่างประเทศ โดยตรวจข้อกำหนดของประเทศปลายทางก่อนเลือกภาษาคำแปล" },
      { q: "Apostille จะช่วยลดขั้นตอนไหม", a: "จะช่วยได้เมื่ออนุสัญญากรุงเฮกมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2570 สำหรับประเทศภาคี ส่วนก่อนหน้านั้นและสำหรับประเทศที่ไม่ใช่ภาคี ยังต้องเดินสายนิติกรณ์และสถานทูตตามเดิม" },
    ],
    feeKeys: ["companyRegistration", "dbdFinancialStatement", "mfaLegalisation"],
    related: [
      { label: "งานเอกสารนิติบุคคลและจดทะเบียนบริษัท", href: "/corporate" },
      { label: "รับรองเอกสารกงสุล", href: "/lgz" },
      { label: "บริการ Notary Public", href: "/notary" },
    ],
    keywords: [
      "แนะนำแปลหนังสือรับรองบริษัทด่วน",
      "แนะนำบริการรับรองเอกสารบริษัท",
      "แนะนำแปลงบการเงินภาษาอังกฤษ",
    ],
  },
];

export const RECOMMEND_TOPIC_BY_SLUG: Record<string, RecommendTopic> = Object.fromEntries(
  RECOMMEND_TOPICS.map((t) => [t.slug, t]),
);

export const RECOMMEND_CTA = CTA;
